import { Anchor } from "@svea/design-system-components-react";
import { Heading, P, Pill, Stack, tw, useText } from "@sveabank/cc-common";
import { Link } from "react-router";
import Card from "../../components/card";
import Image from "../../components/image";
import { useApplicationDetails } from "../../hooks/application";
import { usePageContext } from "../../hooks/context";
import type { DisplayedAddon } from "../../types/product";
import AddonItemActions from "./addon-item-actions";
import AddonItemCheckCredit from "./addon-item-check-credit";
import AddonItemPrice from "./addon-item-price";
import { isCreditWorthy, productImages } from "./helpers";
import { useChoosePackage } from "./hooks";

type Props = {
	addon: DisplayedAddon;
};

const creditProducts = ["CREDITCARD", "CHECKKREDIT"];

const AddonItem = ({ addon }: Props) => {
	const [t, texts] = useText();
	const { selectedAddons, creditScore } = usePageContext(useChoosePackage);
	const { applicationDetails } = useApplicationDetails();

	const isRejected = !creditScore?.assessment || !isCreditWorthy(creditScore.assessment);

	const interestPromotion = applicationDetails?.promotions.find(
		(p) => p.promotionType === "interestRateDiscount",
	);

	const productImage = productImages[addon.sku];
	const unavailable = isRejected && creditProducts.includes(addon.definitionId);

	const isSelected = selectedAddons?.some((a) => a.productDefinitionId === addon.definitionId);
	const addonTexts = texts.addons[addon.sku];
	const title = addonTexts && "title" in addonTexts ? t(addonTexts.title) : "";
	const description = addonTexts && "description" in addonTexts ? t(addonTexts.description) : "";
	const externalLink = addonTexts && "externalLink" in addonTexts ? t(addonTexts.externalLink) : "";
	const moreAboutText = addonTexts && "moreAbout" in addonTexts ? t(addonTexts.moreAbout) : "";

	if (addon.definitionId === "CHECKKREDIT") {
		return <AddonItemCheckCredit addon={addon} />;
	}

	return (
		<div className="relative h-full" data-testid={`addon-card-${addon.definitionId}`}>
			<AddonItemPrice addon={addon} />

			<Card
				direction="col"
				classNames={tw(
					"h-full px-4",
					isSelected && "outline-interactive-primary outline-3 outline-solid",
					!unavailable ? "bg-background-primary" : "bg-background-divider",
				)}
			>
				<Stack gap={6}>
					<div className="px-7">
						{productImage && (
							<Image
								className="h-auto w-full self-center drop-shadow-xs"
								imageSrc={productImage}
								altText={title}
							/>
						)}
					</div>
					{interestPromotion && addon.definitionId === "CREDITCARD" && (
						<Pill
							status="info"
							text={`Din kampanjränta ${creditScore.assessment?.card.interestRate} %`}
						/>
					)}

					<Stack>
						{unavailable ? (
							<P> {t(texts.addons.unavailable)} </P>
						) : (
							<Stack gap={1}>
								<Heading.H4 className="text-text-primary">{title}</Heading.H4>

								<P>{description}</P>
								<Anchor as={Link} to={externalLink} target="_blank" iconEnd="externalLink">
									{moreAboutText}
								</Anchor>
							</Stack>
						)}
					</Stack>
					<div className="mt-auto">
						<Stack>{!unavailable && <AddonItemActions addon={addon} />}</Stack>
					</div>
				</Stack>
			</Card>
		</div>
	);
};

export default AddonItem;
